// @mentions: a message names someone as `@Display Name` in its text. Nothing extra goes on the
// wire; each reader finds the names of the people in the conversation, as they're called now.
import type { ConversationInfo, SpaceInfo, UserProfile } from '../api/types'
import { displayName } from './format'

export interface Person {
  id: string
  name: string
}

/** The people who can be mentioned in a conversation (a channel's whole space), by their current display names. */
export function mentionable(profiles: Map<string, UserProfile>, spaces: readonly SpaceInfo[], conv: ConversationInfo | undefined): Person[] {
  if (!conv) return []
  const ids = conv.kind === 'channel' ? (spaces.find((s) => s.id === conv.space_id)?.members.map((m) => m.profile.user_id) ?? []) : conv.members
  return ids.map((id) => ({ id, name: displayName(profiles, id) }))
}

/** The `@word` being typed just before the caret, if any: where its `@` is and what follows it. */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const m = /(^|\s)@([^\s@]{0,32})$/.exec(text.slice(0, caret))
  return m ? { start: caret - m[2]!.length - 1, query: m[2]! } : null
}

/** People whose name (any word of it) or username starts with the query, best matches first. */
export function suggest(people: readonly Person[], query: string, me: string | null, limit = 6): Person[] {
  const q = query.toLowerCase()
  const rank = (p: Person): number => {
    const name = p.name.toLowerCase()
    if (name.startsWith(q)) return 0
    if (name.split(/\s+/).some((w) => w.startsWith(q))) return 1
    if ((p.id.split('@')[0] ?? '').startsWith(q)) return 2
    return -1
  }
  return people
    .filter((p) => p.id !== me)
    .map((p) => ({ p, r: rank(p) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.p.name.localeCompare(b.p.name))
    .slice(0, limit)
    .map((x) => x.p)
}

export type Segment = { text: string; user?: string }

/**
 * Splits a message's text into plain runs and mentions. A mention is `@` and a person's name
 * (any case, the longest that fits) after the start or a space, ending at a word boundary.
 */
export function splitMentions(body: string, people: readonly Person[]): Segment[] {
  if (!body.includes('@') || !people.length) return [{ text: body }]
  const byLength = [...people].filter((p) => p.name).sort((a, b) => b.name.length - a.name.length)
  const out: Segment[] = []
  let plain = ''
  let i = 0
  while (i < body.length) {
    if (body[i] === '@' && (i === 0 || /\s|[([{"']/.test(body[i - 1]!))) {
      const rest = body.slice(i + 1)
      const hit = byLength.find(
        (p) => rest.slice(0, p.name.length).toLowerCase() === p.name.toLowerCase() && !/[\p{L}\p{N}_]/u.test(rest[p.name.length] ?? ''),
      )
      if (hit) {
        if (plain) out.push({ text: plain })
        plain = ''
        const len = hit.name.length + 1
        out.push({ text: body.slice(i, i + len), user: hit.id })
        i += len
        continue
      }
    }
    plain += body[i]
    i++
  }
  if (plain) out.push({ text: plain })
  return out
}

/** Everyone a message's text mentions, once each, but not its sender. */
export function mentionedIn(body: string, people: readonly Person[], sender: string | null): string[] {
  return [...new Set(splitMentions(body, people).flatMap((s) => (s.user && s.user !== sender ? [s.user] : [])))]
}

/** Whether a message's text mentions this user. */
export function mentions(body: string, people: readonly Person[], user: string | null): boolean {
  return !!user && splitMentions(body, people).some((s) => s.user === user)
}
